import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { from, Observable, of } from 'rxjs';
import { catchError, concatMap, map, mapTo, switchMap, take } from 'rxjs/operators';
import { ShAbstractHttpService } from '../../core/public_api';
import { Chunk } from '../entities/chunk.entity';
import { UploadStatus, UploadUpdate } from '../upload.module';

interface UploadSession {
    chunkSize: number;
    sessionId: string;
    offset: number;
}

@Injectable({ providedIn: 'root' })
export class UploadService {
    private readonly ENDPOINT: string = 'v2/uploads';
    private readonly DRAFT_INTEROP_VERSION: string = '6';

    constructor(private readonly http: ShAbstractHttpService) {}

    public uploadFile(file: File, recordId: string): Observable<UploadUpdate> {
        const session$: Observable<UploadSession> = this.createUploadSession(recordId);

        return session$.pipe(
            switchMap(session => this.startChunkedUpload(file, session)),
            catchError(error => of(this.handleUploadFailure('', error)))
        );
    }

    private createUploadSession(recordId: string): Observable<UploadSession> {
        const endpoint: string = `${this.ENDPOINT}?recordId=${recordId}`;

        const headers = new HttpHeaders()
            .append('Upload-Complete', '?0')
            .append('Upload-Draft-Interop-Version', this.DRAFT_INTEROP_VERSION);

        return this.http
            .postResponse<void>(endpoint, null, headers)
            .pipe(map(response => this.responseToSession(response)));
    }

    public resumeUpload(file: File, sessionId: string): Observable<UploadUpdate> {
        const uploadStatus$: Observable<UploadSession> = this.getUploadStatus(sessionId);
        return uploadStatus$.pipe(
            switchMap(session => this.startChunkedUpload(file, session)),
            catchError(error => of(this.handleUploadFailure('', error)))
        );
    }

    private getUploadStatus(sessionId: string): Observable<UploadSession> {
        const headers = new HttpHeaders().append('Upload-Draft-Interop-Version', this.DRAFT_INTEROP_VERSION);

        return this.http
            .head(`${this.ENDPOINT}/${sessionId}`, undefined, headers)
            .pipe(map(response => this.responseToSession(response, sessionId)));
    }

    private startChunkedUpload(file: File, session: UploadSession): Observable<UploadUpdate> {
        const chunks: Chunk[] = [];

        for (let offset = session.offset; offset < file.size; offset += session.chunkSize) {
            chunks.push(this.readChunk(file, offset, session.chunkSize));
        }

        return from(chunks).pipe(
            concatMap((chunk, index) => this.uploadChunk(session.sessionId, chunk)),
            map((uploadedChunk: Chunk) => this.handleChunkSuccess(session.sessionId, file, uploadedChunk)),
            catchError(error => of(this.handleUploadFailure(session.sessionId, error)))
        );
    }

    private uploadChunk(sessionId: string, chunk: Chunk): Observable<Chunk> {
        const sessionEndpoint: string = `${this.ENDPOINT}/${sessionId}`;

        const headers = new HttpHeaders()
            .append('Upload-Offset', chunk.offset.toString())
            .append('Upload-Complete', chunk.isLastChunk ? '?1' : '?0')
            .append('Upload-Draft-Interop-Version', this.DRAFT_INTEROP_VERSION)
            .append('Content-Type', 'application/partial-upload');

        return this.http.patchResponse<boolean>(sessionEndpoint, chunk.data, headers).pipe(take(1), mapTo(chunk));
    }

    // #region Handlers
    private handleChunkSuccess(sessionId: string, file: File, chunk: Chunk): UploadUpdate {
        const progress: number = ((chunk.offset + chunk.size) / file.size) * 100;
        return {
            sessionId,
            progress,
            status: UploadStatus.UPLOADING
        };
    }

    private handleUploadFailure(sessionId: string, error: Error): UploadUpdate {
        console.error('UploadService:: Uploading failed', error.message);
        return {
            sessionId,
            progress: 100,
            status: navigator.onLine ? UploadStatus.FAILED : UploadStatus.NETWORK_ERROR
        };
    }
    // #endregion

    // #region Helpers
    private responseToSession(response: HttpResponse<void>, sessionId?: string): UploadSession {
        const offset: number = Number(response.headers.get('Upload-Offset'));

        //Get min-append-size value from Upload-Limit: max-size=356172, max-append-size=356172, min-append-size=356172
        const parsedChunkSize: number = Number(response.headers.get('Upload-Limit')?.split('=').pop());
        const chunkSize: number = Number.isNaN(parsedChunkSize) ? 1024 * 1024 * 5 : parsedChunkSize;
        const id: string = sessionId ?? response.headers.get('Location')?.split('/').pop() ?? '';

        return {
            sessionId: id,
            chunkSize,
            offset: offset > 0 ? offset - 1 : 0 // Correction for resumed uploads
        };
    }

    private readChunk(file: File, startOffset: number, chunkSize: number): Chunk {
        const from: number = startOffset;
        const to: number = startOffset + chunkSize >= file.size ? file.size : startOffset + chunkSize;

        const blob: Blob = file.slice(from, to);

        return {
            data: blob,
            offset: startOffset,
            isLastChunk: to >= file.size,
            size: blob.size
        };
    }
    // #endregion
}
